import { Group, Stack, Switch } from '@mantine/core';
import type { RoadmapGoal } from '../../../api/strategy-client/strategy-client.type';
import { useTranslation } from 'react-i18next';
import { SmallSquareButton } from '../../../components/small-square-button/small-square-button';
import { safeParseDateInput } from '../../../helpers/date-time-helpers';
import { secondaryBase } from '../../../styles/design-tokens';
import { Checkbox } from '../../../ui-library/checkbox/checkbox';
import { Divider } from '../../../ui-library/divider/divider';
import { Select } from '../../../ui-library/select/select';
import type { ExtendedComboboxItem } from '../../../ui-library/select/select.type';
import { Text } from '../../../ui-library/typography/typography';
import { DeliveryProgressFilter, YearFilter } from './roadmap.type';

type Props = {
  setShow: (show: boolean) => void;
  title?: string;
  deliveryProgressFilter: DeliveryProgressFilter;
  onDeliveryProgressFilterChange: (filter: DeliveryProgressFilter) => void;
  goals: RoadmapGoal[];
  yearFilter: YearFilter;
  onYearFilterChange: (filter: YearFilter) => void;
  showTodayLine: boolean;
  onShowTodayLineChange: (show: boolean) => void;
};

/**
 * Extract unique years from goals and their initiatives
 */
const extractYearsFromGoals = (goals: RoadmapGoal[]): number[] => {
  const years = new Set<number>();

  goals.forEach((goal) => {
    goal.outcomes?.forEach((outcome) => {
      outcome.initiatives?.forEach((initiative) => {
        if (initiative.start_date) {
          const startDate = safeParseDateInput(initiative.start_date);
          if (startDate) {
            years.add(startDate.year());
          }
        }
        if (initiative.end_date) {
          const endDate = safeParseDateInput(initiative.end_date);
          if (endDate) {
            years.add(endDate.year());
          }
        }
      });
    });
  });

  return Array.from(years).sort((a, b) => b - a);
};

export const RoadmapFilters = ({
  setShow,
  title,
  deliveryProgressFilter,
  onDeliveryProgressFilterChange,
  goals,
  yearFilter,
  onYearFilterChange,
  showTodayLine,
  onShowTodayLineChange,
}: Props) => {
  const i18n = useTranslation();
  const handleFilterChange = (key: keyof DeliveryProgressFilter) => {
    onDeliveryProgressFilterChange({
      ...deliveryProgressFilter,
      [key]: !deliveryProgressFilter[key],
    });
  };

  const allSelected =
    deliveryProgressFilter.notStarted &&
    deliveryProgressFilter.inProgress &&
    deliveryProgressFilter.nearComplete &&
    deliveryProgressFilter.complete;

  const handleToggleAll = () => {
    const newValue = !allSelected;
    onDeliveryProgressFilterChange({
      notStarted: newValue,
      inProgress: newValue,
      nearComplete: newValue,
      complete: newValue,
    });
  };

  // Extract available years and create select options
  const availableYears = extractYearsFromGoals(goals);
  const yearOptions: ExtendedComboboxItem[] = [
    { value: 'all', label: i18n.t('common.all_years', 'All Years') },
    ...availableYears.map((year) => ({
      value: year.toString(),
      label: year.toString(),
    })),
  ];

  const handleYearChange = (value: string) => {
    const selectedYear = value === 'all' ? null : parseInt(value, 10);
    onYearFilterChange({
      selectedYear,
    });
  };

  return (
    <Stack gap={16} h="100%">
      <Group justify="space-between">
        <Text size="medium" weight="medium">
          {title || 'Filters'}
        </Text>
        <SmallSquareButton onClick={() => setShow(false)} iconName="arrow_back_ios" />
      </Group>
      <Divider />

      <Text size="small" weight="medium">
        {i18n.t('common.year', 'Year')}
      </Text>
      <Select
        value={yearFilter.selectedYear ? yearFilter.selectedYear.toString() : 'all'}
        options={yearOptions}
        onChange={handleYearChange}
        placeholder={i18n.t('common.select_year', 'Select a year')}
        size="md"
      />

      <Divider />

      <Text size="small" weight="medium">
        {i18n.t('common.display', 'Display')}
      </Text>
      <Switch
        label="Show Today Line"
        checked={showTodayLine}
        color={secondaryBase}
        style={{ cursor: 'pointer' }}
        onChange={(e) => onShowTodayLineChange(e.currentTarget.checked)}
      />

      <Divider />

      <Text size="small" weight="medium">
        {i18n.t('common.delivery_progress', 'Delivery Progress')}
      </Text>
      <Stack gap="sm">
        <Checkbox label={i18n.t('common.all', 'All')} checked={allSelected} onChange={handleToggleAll} />
        <Checkbox
          label={i18n.t('strategy.filters.not_started', 'Not Started (0%)')}
          checked={deliveryProgressFilter.notStarted}
          onChange={() => handleFilterChange('notStarted')}
        />
        <Checkbox
          label={i18n.t('strategy.filters.in_progress', 'In Progress (1-74%)')}
          checked={deliveryProgressFilter.inProgress}
          onChange={() => handleFilterChange('inProgress')}
        />
        <Checkbox
          label={i18n.t('strategy.filters.near_complete', 'Near Complete (75-99%)')}
          checked={deliveryProgressFilter.nearComplete}
          onChange={() => handleFilterChange('nearComplete')}
        />
        <Checkbox
          label={i18n.t('strategy.filters.complete', 'Complete (100%)')}
          checked={deliveryProgressFilter.complete}
          onChange={() => handleFilterChange('complete')}
        />
      </Stack>
    </Stack>
  );
};
